/**
 * useDealPlaybookEligibility
 *
 * Consulta a RPC `get_deal_per_phase_playbook_eligibility` para decidir se
 * o botão "Iniciar playbook nesta fase" deve ficar habilitado no header do
 * `DealDetailsModalPremium`.
 *
 * CHANGELOG:
 *   2026-06-01 v1.1.0 - Adicionado discriminador `reason` derivado no client
 *                       a partir dos campos já retornados pela RPC. Permite
 *                       que o header exiba o botão sempre (desabilitado quando
 *                       inelegível) com tooltip explicando o motivo. Inclui
 *                       logging diagnóstico para futuras regressões.
 *   2026-06-01 v1.0.0 - Versão inicial.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useDealPlaybookEligibility');

// Estrutura bruta de retorno da RPC
export interface DealPlaybookEligibilityRow {
  playbook_id: string;
  playbook_name: string;
  phase_id: string;
  phase_name: string | null;
  steps_in_phase: number;
  has_active_cadence: boolean;
  is_eligible: boolean;
}

/**
 * Motivos possíveis para o botão estar desabilitado.
 * `eligible` = pronto para acionar; demais valores explicam por quê não.
 */
export type PlaybookEligibilityReason =
  | 'eligible'
  | 'no_playbook'      // workspace sem playbook_mode, deal fechado, fase won/lost
                       // ou 0/>1 playbook per_phase ativo no pipeline (RPC retorna vazio)
  | 'no_steps'         // playbook existe mas não tem steps nesta fase
  | 'cadence_active';  // já existe cadência ativa do mesmo playbook no deal

export interface DealPlaybookEligibility extends DealPlaybookEligibilityRow {
  reason: PlaybookEligibilityReason;
}

// Mensagens humanas (tooltip) por motivo
export const PLAYBOOK_REASON_LABELS: Record<PlaybookEligibilityReason, string> = {
  eligible: 'Iniciar playbook nesta fase',
  no_playbook:
    'Nenhum playbook configurado para este pipeline (ou workspace sem modo playbook)',
  no_steps: 'Este playbook não tem passos configurados para a fase atual',
  cadence_active: 'Já existe uma cadência ativa deste playbook neste negócio',
};

/**
 * Deriva o discriminador `reason` a partir da linha bruta retornada pela RPC.
 * Centraliza a regra para que o componente apenas leia a string final.
 */
function deriveReason(
  row: DealPlaybookEligibilityRow | null,
): { row: DealPlaybookEligibilityRow | null; reason: PlaybookEligibilityReason } {
  if (!row) return { row: null, reason: 'no_playbook' };
  if (row.is_eligible) return { row, reason: 'eligible' };
  if (row.has_active_cadence) return { row, reason: 'cadence_active' };
  if ((row.steps_in_phase ?? 0) === 0) return { row, reason: 'no_steps' };
  // Fallback defensivo
  return { row, reason: 'no_playbook' };
}

/**
 * Retorna a elegibilidade com motivo derivado. Quando a RPC não devolve
 * linha (deal inelegível por workspace/fase/status/ambiguidade), retorna
 * um objeto sintético com `reason='no_playbook'` para que o header consiga
 * exibir o botão desabilitado com tooltip.
 */
export function useDealPlaybookEligibility(dealId: string | null | undefined) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useQuery({
    queryKey: ['deal-playbook-eligibility', dealId, tenantId],
    queryFn: async (): Promise<DealPlaybookEligibility> => {
      logger.debug('Consultando elegibilidade de playbook', { dealId, tenantId });

      const { data, error } = await (supabase.rpc as any)(
        'get_deal_per_phase_playbook_eligibility',
        { p_deal_id: dealId, p_tenant_hint: tenantId },
      );

      if (error) {
        logger.error('Erro ao consultar elegibilidade de playbook:', error);
        throw error;
      }

      const row = (Array.isArray(data) ? data[0] : data) as
        | DealPlaybookEligibilityRow
        | null
        | undefined;

      const { row: normalizedRow, reason } = deriveReason(row ?? null);

      logger.debug('Resposta da RPC de elegibilidade', { row: normalizedRow, reason });

      // Quando não há linha, devolvemos um objeto sintético inelegível
      return {
        playbook_id: normalizedRow?.playbook_id ?? '',
        playbook_name: normalizedRow?.playbook_name ?? '',
        phase_id: normalizedRow?.phase_id ?? '',
        phase_name: normalizedRow?.phase_name ?? null,
        steps_in_phase: normalizedRow?.steps_in_phase ?? 0,
        has_active_cadence: normalizedRow?.has_active_cadence ?? false,
        is_eligible: normalizedRow?.is_eligible ?? false,
        reason,
      };
    },
    enabled: !!dealId && !!tenantId,
    staleTime: 30_000,
  });
}
